import { trackingClient } from '../clients'
import { TrackingEventTypeEnum } from '../common/enum'
import { FormatDateFunction, TranslationFunction } from '../common/types'
import { SearchbarFieldsReturn } from '../components/property-details/hooks/use-searchbar'
import { FormattedLivePricingDataTransport } from '../models/pricing/live-pricing-formatter'
import { CookieManagerService } from '../services'
import AnalyticsService from '../services/analytics-service'
import { pdpPageActions } from '../services/analytics-service/actions'
import { TrackingEvent } from '../transports/tracking-transport'
import { ListingSectionObject } from '../transports/common/listing-section-transport'
import { Provider } from '../models/property-details-page/contact-model'
import { OfferTypes } from '../models/common/offers-model'
import get from 'lodash/get'
import { enqueueViewEvents } from './tracking-queue'
import { SRL_PAGE_SIZE } from '../components/utils'
import { format } from '../hooks/date'
import { LOCALES } from '../common/constants'
import { getCategory } from './category-utils'

export const trackEvents = (events: TrackingEvent[]) => {
    trackingClient.trackEvents({ trackingEvents: events }).then()
}

const formatSearchDate = (date: Date, format: FormatDateFunction): string =>
    `day=${format(date, 'dd')}|month=${format(date, 'MM')}|year=${format(date, 'yyyy')}`

export function trackSrlObjectClick({
    listingId,
    searchbar,
    pricingDetails,
    pricingErrorCode,
    t,
    format,
    isComboListing,
    propertyTitle,
    propertyFullTitle,
    providerId,
    searchRequestId,
}: {
    listingId: string
    searchbar: SearchbarFieldsReturn
    pricingDetails: FormattedLivePricingDataTransport | null
    pricingErrorCode: number | null
    t: TranslationFunction
    format: FormatDateFunction
    isComboListing: boolean
    propertyTitle?: string
    propertyFullTitle?: string
    providerId?: number
    searchRequestId?: string
}) {
    const cookieKey = `srlObjectClicked_${listingId}`
    const componentCookieKey = `srlObjectClicked_${listingId}_componentName`

    const srlReferer = CookieManagerService.getCookie(cookieKey)
    const componentName = CookieManagerService.getCookie(componentCookieKey)

    const arrival = searchbar.arrival ? format(searchbar.arrival, 'yyyy-MM-dd') : null
    const departure = searchbar.departure ? format(searchbar.departure, 'yyyy-MM-dd') : null

    const filters = {
        arrival_date: arrival,
        departure_date: departure,
        adult: searchbar.adults,
        children: searchbar.kids,
    }

    const listing = {
        tfw_object_id: listingId,
        availability: pricingErrorCode ? 'not_available' : pricingDetails?.price ? 'available' : 'on_request',
        final_price: get(pricingDetails, 'formattedPrice'),
        error_message: pricingErrorCode ? t(`livePriceErrors.${pricingErrorCode}`) : '',
        component: componentName || '',
    }

    const eventData = {
        url: srlReferer,
        timestamp: new Date(),
        filters,
        listing,
        ...(searchRequestId && { search_request_id: searchRequestId }),
    }

    trackEvents([
        {
            eventType: TrackingEventTypeEnum.EVENT_RESULT_OBJECT_CLICK,
            eventValue: Number(listingId),
            eventData: eventData,
        },
    ])

    // GTM view_item event
    const searchDateFrom = searchbar.arrival ? formatSearchDate(searchbar.arrival, format) : undefined
    const searchDateTo = searchbar.departure ? formatSearchDate(searchbar.departure, format) : undefined

    const persons = searchbar?.adults + searchbar?.kids || undefined

    AnalyticsService.trackEvent({
        event: pdpPageActions.VIEW_ITEM,
        ecommerce: {
            items: [
                {
                    item_id: listingId,
                    item_name: propertyTitle,
                    affiliation: providerId,
                    item_category: getCategory(isComboListing),
                    price: get(pricingDetails, 'price'),
                    currency: get(pricingDetails, 'currency'),
                    quantity: 1,
                },
            ],
        },
        arrival: arrival,
        departure: departure,
        search_date_from: searchDateFrom,
        search_date_to: searchDateTo,
        persons: persons,
        page_referrer: srlReferer,
        page_title: propertyFullTitle,
        price_availability: listing.availability,
        ...(searchRequestId && { search_request_id: searchRequestId }),
        ...(pricingErrorCode !== null && {
            pricing_error_code: pricingErrorCode,
            pricing_error_message: t(`livePriceErrors.${pricingErrorCode}`),
        }),
    })

    CookieManagerService.deleteCookie(cookieKey)
    CookieManagerService.deleteCookie(componentCookieKey)
}

export const trackEventsV2 = (events: TrackingEvent[]) => {
    trackingClient.trackEventsV2({ trackingEvents: events }).then()
}

export interface SrlFilterData {
    adult: number
    children: number
    arrival_date: string | null
    departure_date: string | null
    search_request_id?: string
}

const getProviderName = (providerId?: string): string | null => {
    if (!providerId) {
        return null
    }
    const id = Number(providerId)
    return Provider[id] ?? null
}

const getOfferTypeName = (types?: OfferTypes[]): string | null => {
    if (!types || types.length === 0) {
        return null
    }
    return OfferTypes[types[0]] ?? null
}

const buildSrlEventData = (listing: ListingSectionObject, index: number, filterData: SrlFilterData) => {
    const date = new Date()
    const dateString = format(date, 'yyyy-MM-dd')
    const timeString = date.toLocaleTimeString(LOCALES.DE_DE, { hour12: false })

    return {
        url: window.location.href,
        filters: {
            region_title: listing?.seoRegion?.name ?? '',
            region_id: listing?.regionId ?? '',
            adult: filterData.adult,
            arrival_date: filterData.arrival_date,
            children: filterData.children,
            departure_date: filterData.departure_date,
        },
        listing: {
            tfw_object_id: String(listing.id),
            provider_id: listing.providerId,
            provider_title: getProviderName(listing.providerId),
            is_premium_top_listing: !!listing.isPremiumTopObject,
            availability: listing.price ? 'available' : 'on_request',
            external_object_id: listing.externalObjectId,
            features: listing.generalEquipments
                ? Object.entries(listing.generalEquipments).flatMap(([k, v]) => (v ? [k] : []))
                : [],
            index: index,
            is_combo_listing: listing.isComboListing,
            page: Math.ceil(index / SRL_PAGE_SIZE) || 1,
            price: listing.price
                ? {
                      final_price: listing.price.price ?? null,
                      discount_in_percent: listing.offer?.discount ? String(listing.offer.discount) : null,
                      discount_type: getOfferTypeName(listing.offer?.types),
                      discount_from: listing.offer?.dateFrom ?? null,
                      discount_to: listing.offer?.dateTo ?? null,
                  }
                : null,
            component: {
                name: 'srl_result',
            },
        },
        google_analytics: [
            {
                key: '_gid',
                value: CookieManagerService.getCookie('_gid'),
            },
            {
                key: '_ga',
                value: CookieManagerService.getCookie('_ga'),
            },
        ],
        timestamp: `${dateString} ${timeString}`,
        ...(filterData.search_request_id && { search_request_id: filterData.search_request_id }),
    }
}

export const trackSrlListingViewV2 = (listing: ListingSectionObject, index: number, filterData: SrlFilterData) => {
    if (listing.externalPartnerInfo) {
        return
    }

    const eventData = buildSrlEventData(listing, index, filterData)
    const eventValue = Number(listing.id)
    const events: TrackingEvent[] = []

    // Always fire EVENT_RESULT_OBJECT_VIEW (21)
    events.push({
        eventType: TrackingEventTypeEnum.EVENT_RESULT_OBJECT_VIEW,
        eventValue,
        eventData,
    })

    // If top listing, also fire EVENT_TOP_PROMOTION_VIEW (13)
    if (listing.isTopObject || listing.isPremiumTopObject) {
        events.push({
            eventType: TrackingEventTypeEnum.EVENT_TOP_PROMOTION_VIEW,
            eventValue,
            eventData,
        })
    }

    // View events are batched — collected across all visible cards and flushed
    // in a single API call after a short debounce window.
    enqueueViewEvents(events)
}

export const trackSrlListingClickV2 = (listing: ListingSectionObject, index: number, filterData: SrlFilterData) => {
    if (listing.externalPartnerInfo) {
        return
    }

    const eventData = buildSrlEventData(listing, index, filterData)
    const eventValue = Number(listing.id)
    const events: TrackingEvent[] = []

    // Always fire EVENT_RESULT_OBJECT_CLICK (22)
    events.push(
        {
            eventType: TrackingEventTypeEnum.EVENT_RESULT_OBJECT_CLICK,
            eventValue,
            eventData,
        },
        {
            eventType: TrackingEventTypeEnum.EVENT_RESULT_VIEW,
            eventValue,
            eventData,
        },
    )

    // If top listing, also fire EVENT_TOP_PROMOTION_CLICK (14)
    if (listing.isTopObject || listing.isPremiumTopObject) {
        events.push({
            eventType: TrackingEventTypeEnum.EVENT_TOP_PROMOTION_CLICK,
            eventValue,
            eventData,
        })
    }

    trackEventsV2(events)
}
